import type {Datatype, EnumValues, FieldTypeId, Fond, LabeledIdentified, Ordered, SimpleFond} from 'src/typings';
import type {ValueEditorBindings} from 'shared/value-editors/kp-value-editor/types';

export interface Columnable {
    fieldTypes: FieldTypeDefinition[];
}

// Number format
export const NUMBER_VIEW_FORMAT_RESPONSE = {
    STANDARD: 'STANDARD',
    PERCENTAGE: 'PERCENTAGE'
} as const;
export type NumberViewFormatResponse = EnumValues<typeof NUMBER_VIEW_FORMAT_RESPONSE>;

export interface NumberValueFormatResponse {
    type: 'number_value_format';
    viewFormat: NumberViewFormatResponse;
}

// Datetime range format
export const DATETIME_GRANULARITY_RESPONSE = {
    DATE: 'date'
} as const;

export type DatetimeGranularityResponse = EnumValues<typeof DATETIME_GRANULARITY_RESPONSE>

export interface DatetimeRangeValueFormatResponse {
    type: 'datetime_range_value_format';
    granularity: DatetimeGranularityResponse;
}

// Text format
export const TEXT_VIEW_FORMAT_RESPONSE = {
    SIMPLE_TEXT: 'simple_text',
    MARKDOWN: 'markdown'
} as const;

export const TEXT_VIEW_SIZE_RESPONSE = {
    ONELINE: 'oneline',
    MULTILINE: 'multiline'
} as const;

export type TextViewFormatResponse = EnumValues<typeof TEXT_VIEW_FORMAT_RESPONSE>;
export type TextViewSizeResponse = EnumValues<typeof TEXT_VIEW_SIZE_RESPONSE>;

export interface TextValueFormatResponse {
    type: 'text_value_format';
    viewFormat: TextViewFormatResponse;
    viewSize: TextViewSizeResponse;
}

export type ValueFormatResponse =
    DatetimeRangeValueFormatResponse
    | NumberValueFormatResponse
    | TextValueFormatResponse;

export interface FieldTypeDefinition extends LabeledIdentified<FieldTypeId>, Ordered, Columnable {
    valueDatatype?: Datatype;
    valueFormat?: ValueFormatResponse;
    enabled: FondedAttribute<boolean>;
    editable: FondedAttribute<boolean>;
    required: FondedAttribute<boolean>;
    regenerable: FondedAttribute<boolean>;
    recordReferenceProperties: FondedAttribute<RecordReferenceProperties | null>;
    editor: FondedAttribute<ValueEditorBindings | null>;
    staticDependentFieldTypeIds: FieldTypeId[];
    valueSorter?: string;
}

export interface FondedAttribute<T> {
    defaultValue: T;
    fondedValues: Record<number, T>;
}

export type FondFieldTypeDefinitions = Columnable;

export interface RecordReferenceProperties {
    parentable: boolean,
    rootFond: SimpleFond,
    possibleReferenceTargetFonds: Fond[]
}

export interface RelatedRecordsDescriptor {
    createParams: {
        possibleReferenceTargetFonds: Fond[];
        fieldTypeId: FieldTypeId;
    };
    searchParams: {
        dynamicFieldSearchParamName: string;
    };
}

interface FondedRowSettings {
    relatedRecordsDescriptors: FondedAttribute<RelatedRecordsDescriptor[]>;
}

export interface GridLayout extends Columnable {
    fondedRowSettings: FondedRowSettings
}

export interface SubRowCreateParam {
    fieldTypeId: FieldTypeId;
    referenceTargetFond: Fond;
}